import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { Container, PageTitle, NbButton, NbCard, Chip } from "@/components/nb";
import { Send, Sparkles, Play, X, Search } from "lucide-react";

const SUBJECTS = ["General", "Math", "Physics", "Chemistry", "Biology", "English", "History", "Geography", "Economics", "Civics", "Psychology", "Computer Science", "Business", "Accountancy", "GK", "Coding", "Career"];

function makeSessionId() {
  let s = localStorage.getItem("sl_doubt_sess");
  if (!s) { s = "doubt-" + Math.random().toString(36).slice(2, 10); localStorage.setItem("sl_doubt_sess", s); }
  return s;
}

export default function DoubtChat() {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [subject, setSubject] = useState("General");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(makeSessionId());
  const [video, setVideo] = useState(null);
  const [openVideo, setOpenVideo] = useState(null);
  const [subjectSearch, setSubjectSearch] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get(`/study/doubt/history/${sessionId}`).then((r) => {
      const flat = [];
      r.data.forEach((m) => {
        flat.push({ role: "user", text: m.user_message });
        flat.push({ role: "assistant", text: m.assistant_reply });
      });
      setMsgs(flat);
    }).catch(() => {});
  }, [sessionId]);

  // Handle prefill from Study Plan
  useEffect(() => {
    const raw = sessionStorage.getItem("sl_chat_prefill");
    if (!raw) return;
    try {
      const { prompt, subject: subj } = JSON.parse(raw);
      sessionStorage.removeItem("sl_chat_prefill");
      if (subj) setSubject(subj);
      setInput(prompt || "");
      // Auto-send after a tiny delay so state settles
      setTimeout(() => {
        const btn = document.querySelector('[data-testid="chat-send"]');
        if (btn) btn.click();
      }, 150);
    } catch { /* ignore */ }
    // eslint-disable-next-line
  }, []);

  useEffect(() => { scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" }); }, [msgs, loading, video]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;
    const q = input.trim();
    setInput("");
    setMsgs((m) => [...m, { role: "user", text: q }]);
    setLoading(true);
    try {
      const r = await api.post("/study/doubt/chat", { session_id: sessionId, subject, message: q });
      setMsgs((m) => [...m, { role: "assistant", text: r.data.reply }]);
      if (r.data.suggested_video) setVideo(r.data.suggested_video);
    } catch (err) {
      setMsgs((m) => [...m, { role: "assistant", text: "Oops, couldn't reach Doubt-Buddy right now." }]);
    } finally { setLoading(false); }
  };

  return (
    <Container>
      <PageTitle eyebrow="DOUBT-CLEARING CHAT" title="Ask anything. Get it, actually." sub="Doubt-Buddy explains it, quizzes you back, and drops a curated video." />
      <div className="flex items-center gap-2 bg-white nb-border nb-shadow rounded-xl px-3 py-2 max-w-md mb-3">
        <Search className="w-4 h-4 shrink-0" />
        <input data-testid="subject-search-chat" value={subjectSearch} onChange={(e) => setSubjectSearch(e.target.value)} placeholder="Search a subject..." className="flex-1 bg-transparent outline-none py-1 text-sm" />
      </div>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="subject-picker">
        {SUBJECTS.filter((s) => s.toLowerCase().includes(subjectSearch.trim().toLowerCase())).map((s) => (
          <button key={s} onClick={() => setSubject(s)} data-testid={`subj-${s}`} className={`px-3 py-1 rounded-full nb-border font-mono-sl text-[11px] uppercase ${subject === s ? "bg-sl-primary nb-shadow" : "bg-white"}`}>{s}</button>
        ))}
        {subjectSearch && !SUBJECTS.some((s) => s.toLowerCase() === subjectSearch.trim().toLowerCase()) && subjectSearch.trim() && (
          <button onClick={() => setSubject(subjectSearch.trim())} data-testid="subj-custom" className={`px-3 py-1 rounded-full nb-border font-mono-sl text-[11px] uppercase ${subject === subjectSearch.trim() ? "bg-sl-primary nb-shadow" : "bg-sl-tertiary"}`}>
            Use "{subjectSearch.trim()}"
          </button>
        )}
      </div>

      <NbCard className="!p-0 overflow-hidden">
        <div ref={scrollRef} data-testid="chat-scroll" className="h-[55vh] overflow-y-auto px-6 py-5 bg-sl-bg">
          {msgs.length === 0 && (
            <div className="text-center py-16 text-neutral-500">
              <Sparkles className="w-8 h-8 mx-auto mb-3" />
              <div className="font-heading font-bold">Try: <span className="italic">"Explain quadratic equations like I'm 12."</span></div>
            </div>
          )}
          <AnimatePresence initial={false}>
            {msgs.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.25 }}
                className={`flex mb-4 ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div data-testid={`msg-${m.role}-${i}`} className={`max-w-[85%] px-4 py-3 rounded-xl nb-border ${m.role === "user" ? "bg-sl-primary" : "bg-white"} whitespace-pre-wrap`}>
                  {m.text}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {video && !loading && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              {video.youtube_id ? (
                <button onClick={() => setOpenVideo(video)} data-testid="suggested-video" className="w-full text-left">
                  <div className="bg-sl-accent nb-border rounded-xl nb-shadow p-4 flex items-center gap-3 nb-hover">
                    <div className="w-14 h-14 bg-white nb-border rounded-lg grid place-items-center"><Play className="w-6 h-6" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono-sl text-[10px] uppercase">RECOMMENDED VIDEO</div>
                      <div className="font-heading font-bold truncate">{video.title}</div>
                    </div>
                    <Chip color="tertiary">WATCH</Chip>
                  </div>
                </button>
              ) : (
                <a href={video.search_url} target="_blank" rel="noreferrer" data-testid="suggested-video-search" className="block">
                  <div className="bg-sl-accent nb-border rounded-xl nb-shadow p-4 flex items-center gap-3 nb-hover">
                    <div className="w-14 h-14 bg-white nb-border rounded-lg grid place-items-center"><Play className="w-6 h-6" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono-sl text-[10px] uppercase">SEARCH YOUTUBE</div>
                      <div className="font-heading font-bold truncate">{video.title || video.query}</div>
                    </div>
                    <Chip color="tertiary">OPEN</Chip>
                  </div>
                </a>
              )}
            </motion.div>
          )}

          {loading && (
            <div className="flex justify-start">
              <div className="px-4 py-3 rounded-xl nb-border bg-white font-mono-sl text-sm">
                <span className="inline-block animate-pulse">Doubt-Buddy is thinking</span>
                <span className="inline-block animate-bounce ml-1">.</span>
                <span className="inline-block animate-bounce ml-0.5" style={{ animationDelay: "120ms" }}>.</span>
                <span className="inline-block animate-bounce ml-0.5" style={{ animationDelay: "240ms" }}>.</span>
              </div>
            </div>
          )}
        </div>
        <form onSubmit={send} className="flex gap-2 p-3 bg-white border-t-2 border-black">
          <input data-testid="chat-input" className="flex-1 px-4 py-3 nb-border rounded-lg bg-white" placeholder={`Ask about ${subject}...`} value={input} onChange={(e) => setInput(e.target.value)} />
          <NbButton type="submit" color="black" disabled={loading} data-testid="chat-send"><Send className="w-4 h-4 inline-block" /></NbButton>
        </form>
      </NbCard>

      {openVideo && (
        <div className="fixed inset-0 bg-black/70 z-50 grid place-items-center p-5" onClick={() => setOpenVideo(null)} data-testid="video-modal">
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-full max-w-4xl bg-white nb-border nb-shadow-xl rounded-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b-2 border-black flex justify-between items-center">
              <div className="font-heading font-bold truncate">{openVideo.title}</div>
              <button onClick={() => setOpenVideo(null)} data-testid="close-video" className="p-2 nb-border rounded-lg bg-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="aspect-video bg-black">
              <iframe title={openVideo.title} className="w-full h-full" src={`https://www.youtube.com/embed/${openVideo.youtube_id}?rel=0&autoplay=1`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          </motion.div>
        </div>
      )}
    </Container>
  );
}
